import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { formatViews } from "@/lib/media";

interface DetailSidebarProps {
  item: MediaItem;
  related: MediaItem[];
}

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex gap-3 py-2 text-sm">
    <span className="w-14 shrink-0 text-foreground-500">{label}</span>
    <span className="flex-1 text-foreground-200">{children}</span>
  </div>
);

export default function DetailSidebar({ item, related }: DetailSidebarProps) {
  return (
    <aside className="space-y-6" aria-label="影片信息与相关推荐">
      <section className="rounded-lg border border-background-800 bg-background-900/50 p-5">
        <h2 className="font-heading text-base font-semibold text-foreground-50">影片信息</h2>
        <div className="mt-2 divide-y divide-background-800">
          <InfoRow label="上映年份">{item.year} 年</InfoRow>
          <InfoRow label="地区">{item.region}</InfoRow>
          <InfoRow label="类型">{item.genres.join(" / ")}</InfoRow>
          <InfoRow label="导演">
            <span className="text-accent-300">{item.director}</span>
          </InfoRow>
          <InfoRow label="主演">{item.cast.join("、")}</InfoRow>
          {item.type === "movie" ? (
            <InfoRow label="片长">{item.duration}</InfoRow>
          ) : (
            <InfoRow label="集数">{item.episodes?.length ?? 0} 集</InfoRow>
          )}
          <InfoRow label="热度">
            <span className="flex items-center gap-1 text-accent-300">
              <i className="ri-fire-line"></i>
              {formatViews(item.views)}
            </span>
          </InfoRow>
          <InfoRow label="评分">
            <span className="flex items-center gap-1 text-accent-300">
              <i className="ri-star-fill"></i>
              {item.rating.toFixed(1)} / 10
            </span>
          </InfoRow>
        </div>
      </section>

      <section className="rounded-lg border border-background-800 bg-background-900/50 p-5">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-50">
          <i className="ri-magic-line text-accent-400"></i>
          相关推荐
        </h2>
        <ul className="mt-3 space-y-3">
          {related.map((entry) => (
            <li key={entry.id}>
              <Link to={`/detail/${entry.id}`} className="group flex gap-3">
                <span className="block h-24 w-16 shrink-0 overflow-hidden rounded-md bg-background-800">
                  <img
                    src={entry.cover}
                    alt={`《${entry.title}》封面`}
                    title={`${entry.title} - 免费播放片大片`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground-100 transition-colors group-hover:text-accent-400">
                    {entry.title}
                  </span>
                  <span className="mt-1 flex items-center gap-1 text-xs text-accent-400">
                    <i className="ri-star-fill text-[11px]"></i>
                    {entry.rating.toFixed(1)}
                  </span>
                  <span className="mt-1 block truncate text-xs text-foreground-400">
                    {entry.year} · {entry.genres.slice(0, 2).join(" / ")}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
}